import { motion } from "framer-motion";
import { ArrowRight, Phone, Sparkles, Star } from "lucide-react";
import { CONTACT, LOGO_URL } from "./constants";

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  show: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.7, delay: i * 0.08, ease: [0.22, 0.61, 0.36, 1] },
  }),
};

export default function Hero() {
  return (
    <section
      id="anasayfa"
      data-testid="hero-section"
      className="relative pt-32 pb-24 sm:pt-40 sm:pb-32 overflow-hidden"
    >
      {/* background layers */}
      <div className="absolute inset-0 ab-grid-bg opacity-60" aria-hidden />
      <div className="absolute inset-0 ab-radial-glow" aria-hidden />
      <div
        className="absolute -top-32 -right-32 w-[520px] h-[520px] rounded-full blur-3xl opacity-30"
        style={{
          background:
            "radial-gradient(circle at center, #2D3FFF 0%, transparent 70%)",
        }}
        aria-hidden
      />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        <div className="grid lg:grid-cols-12 gap-12 items-center">
          {/* Copy */}
          <div className="lg:col-span-7">
            <motion.div
              variants={fadeUp}
              initial="hidden"
              animate="show"
              custom={0}
            >
              <span className="ab-chip">
                <Sparkles className="h-3.5 w-3.5" />
                Dijital Çözümler Ajansı
              </span>
            </motion.div>

            <motion.h1
              variants={fadeUp}
              initial="hidden"
              animate="show"
              custom={1}
              className="font-display mt-6 text-5xl sm:text-6xl lg:text-7xl font-extrabold leading-[1.02] text-[var(--ab-navy)]"
            >
              Dijitalde Büyümenin
              <br />
              <span className="ab-text-gradient">Profesyonel</span> Yolu.
            </motion.h1>

            <motion.p
              variants={fadeUp}
              initial="hidden"
              animate="show"
              custom={2}
              className="mt-6 text-lg sm:text-xl text-slate-600 max-w-2xl leading-relaxed"
            >
              İşletmenize özel <strong className="text-[var(--ab-navy)]">web sitesi tasarımı</strong>, hedefli{" "}
              <strong className="text-[var(--ab-navy)]">Google &amp; Instagram reklam yönetimi</strong>,{" "}
              <strong className="text-[var(--ab-navy)]">WhatsApp otomasyonu</strong>, randevu ve sipariş takip
              sistemleri ile rakiplerinizin bir adım önüne geçin.
            </motion.p>

            <motion.div
              variants={fadeUp}
              initial="hidden"
              animate="show"
              custom={3}
              className="mt-9 flex flex-col sm:flex-row gap-3"
            >
              <a
                href={CONTACT.whatsappUrl}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="hero-whatsapp-btn"
                className="ab-btn-primary inline-flex items-center justify-center gap-2 px-7 py-4 rounded-full text-sm font-semibold"
              >
                WhatsApp&apos;tan Ulaş
                <ArrowRight className="h-4 w-4" />
              </a>
              <a
                href={CONTACT.phoneLink}
                data-testid="hero-phone-btn"
                className="ab-btn-secondary inline-flex items-center justify-center gap-2 px-7 py-4 rounded-full text-sm font-semibold"
              >
                <Phone className="h-4 w-4" />
                Hemen Ara: {CONTACT.phoneDisplay}
              </a>
            </motion.div>

            <motion.div
              variants={fadeUp}
              initial="hidden"
              animate="show"
              custom={4}
              className="mt-10 flex flex-wrap items-center gap-6 text-sm text-slate-500"
            >
              <div className="flex items-center gap-2">
                <div className="flex -space-x-1.5">
                  {[1, 2, 3, 4].map((n) => (
                    <div
                      key={n}
                      className="w-7 h-7 rounded-full border-2 border-white"
                      style={{
                        background: `linear-gradient(135deg, hsl(${n * 60} 70% 60%), hsl(${n * 60 + 40} 70% 55%))`,
                      }}
                    />
                  ))}
                </div>
                <span>
                  <strong className="text-[var(--ab-navy)]">120+</strong> mutlu müşteri
                </span>
              </div>
              <div className="flex items-center gap-1.5">
                {[1, 2, 3, 4, 5].map((n) => (
                  <Star
                    key={n}
                    className="h-4 w-4 fill-amber-400 text-amber-400"
                  />
                ))}
                <span className="ml-1">
                  <strong className="text-[var(--ab-navy)]">4.9/5</strong> müşteri puanı
                </span>
              </div>
            </motion.div>
          </div>

          {/* Visual */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.9, delay: 0.2 }}
            className="lg:col-span-5 relative"
          >
            <div className="relative">
              <div
                className="absolute -inset-6 rounded-[2rem] blur-2xl opacity-40"
                style={{
                  background:
                    "linear-gradient(135deg, #2D3FFF, #5A3FFF)",
                }}
                aria-hidden
              />
              <div className="relative rounded-3xl border border-slate-200 bg-white shadow-[0_30px_80px_-30px_rgba(10,19,48,0.30)] overflow-hidden">
                {/* Stat panel */}
                <div className="p-6 sm:p-8">
                  <div className="flex items-center gap-3 mb-6">
                    <img
                      src={LOGO_URL}
                      alt="AB"
                      className="h-12 w-12 object-contain rounded-xl"
                    />
                    <div>
                      <div className="text-xs uppercase tracking-widest text-slate-500">
                        Kampanya Performansı
                      </div>
                      <div className="text-sm font-semibold text-[var(--ab-navy)]">
                        Aralık 2025
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    {[
                      { k: "Tıklama", v: "84.2K", c: "+%212" },
                      { k: "Dönüşüm", v: "3.140", c: "+%148" },
                      { k: "Bütçe Verimi", v: "₺0.42", c: "-%38" },
                      { k: "ROAS", v: "6.8x", c: "+%92" },
                    ].map((s) => (
                      <div
                        key={s.k}
                        className="rounded-xl border border-slate-200 bg-slate-50/70 p-4"
                      >
                        <div className="text-xs text-slate-500">{s.k}</div>
                        <div className="font-display text-2xl font-bold text-[var(--ab-navy)] mt-1">
                          {s.v}
                        </div>
                        <div
                          className={`text-xs font-semibold mt-1 ${
                            s.c.startsWith("-")
                              ? "text-emerald-600"
                              : "text-[var(--ab-blue)]"
                          }`}
                        >
                          {s.c} bu hafta
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Mini chart */}
                  <div className="mt-6 rounded-xl border border-slate-200 bg-white p-4">
                    <div className="flex items-center justify-between mb-3">
                      <div className="text-xs text-slate-500 uppercase tracking-widest">
                        Dönüşüm Trendi
                      </div>
                      <div className="text-xs font-semibold text-[var(--ab-blue)]">
                        +%240
                      </div>
                    </div>
                    <svg viewBox="0 0 280 80" className="w-full h-20">
                      <defs>
                        <linearGradient id="ab-spark" x1="0" x2="0" y1="0" y2="1">
                          <stop offset="0%" stopColor="#2D3FFF" stopOpacity="0.35" />
                          <stop offset="100%" stopColor="#2D3FFF" stopOpacity="0" />
                        </linearGradient>
                      </defs>
                      <path
                        d="M0,65 L30,58 L60,55 L90,48 L120,52 L150,40 L180,30 L210,26 L240,18 L280,8 L280,80 L0,80 Z"
                        fill="url(#ab-spark)"
                      />
                      <path
                        d="M0,65 L30,58 L60,55 L90,48 L120,52 L150,40 L180,30 L210,26 L240,18 L280,8"
                        fill="none"
                        stroke="#2D3FFF"
                        strokeWidth="2.5"
                        strokeLinecap="round"
                      />
                    </svg>
                  </div>
                </div>
              </div>

              {/* Floating accent card */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.7 }}
                className="absolute -bottom-6 -left-6 hidden sm:flex items-center gap-3 bg-[var(--ab-navy)] text-white rounded-2xl px-5 py-4 shadow-2xl"
              >
                <div className="w-10 h-10 rounded-full bg-[var(--ab-blue)] flex items-center justify-center">
                  <Sparkles className="h-5 w-5" />
                </div>
                <div>
                  <div className="text-[0.65rem] uppercase tracking-widest text-blue-200">
                    Yeni Müşteri
                  </div>
                  <div className="font-semibold text-sm">Az önce sipariş alındı</div>
                </div>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
